import { useState } from 'react';
import { MapPin, Globe, Plane, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { COUNTRIES, COUNTRIES_DATA } from './tripConstants';

export const DestinationStep = ({
  formData,
  setFormData,
  updateFormData,
  handleCountryChange,
  removeCountry,
  toggleCitySelection,
}) => {
  const [customCityInput, setCustomCityInput] = useState('');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold to-ocean flex items-center justify-center text-white shadow-lg">
          <MapPin className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-ocean">Where are you going?</h3>
          <p className="text-ocean/70 text-sm">Select one or multiple countries, then pick cities</p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="bg-gradient-to-br from-ocean/10 to-sky/10 p-5 rounded-xl border border-ocean/20 backdrop-blur-sm">
          <Label htmlFor="country" className="text-ocean font-medium flex items-center gap-2">
            <Globe className="w-4 h-4 text-sky" /> Add Country
          </Label>
          <Select value={formData.destination_country} onValueChange={handleCountryChange}>
            <SelectTrigger className="input-field mt-2 bg-white/70" data-testid="country-select">
              <SelectValue placeholder="Select a country" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {COUNTRIES.map((country) => (
                <SelectItem key={country} value={country}>{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {formData.destination_countries.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {formData.destination_countries.map(country => (
                <span key={country} className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-ocean to-sky text-white rounded-full text-sm font-medium">
                  {country}
                  <button onClick={() => removeCountry(country)} className="ml-1 hover:text-red-200 transition-colors" data-testid={`remove-country-${country}`}>
                    &times;
                  </button>
                </span>
              ))}
              {formData.destination_countries.length > 1 && (
                <span className="text-xs text-ocean/60 self-center ml-2">Multi-country trip!</span>
              )}
            </div>
          )}
        </div>

        {formData.destination_countries.length > 0 && formData.destination_countries.map(country => (
          <div key={country} className="bg-gradient-to-br from-sky/10 to-gold/10 p-5 rounded-xl border border-sky/20 backdrop-blur-sm">
            <Label className="text-ocean font-medium flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-gold" /> {country} - Select Cities
              <span className="text-xs text-ocean/50 font-normal">(click to select multiple)</span>
            </Label>
            <div className="flex flex-wrap gap-2">
              {(COUNTRIES_DATA[country] || []).map((city) => (
                <button
                  key={city}
                  onClick={() => toggleCitySelection(city)}
                  className={cn(
                    'px-4 py-2 rounded-full border-2 transition-all duration-200 text-sm font-medium',
                    formData.destination_cities.includes(city)
                      ? 'border-gold bg-gradient-to-r from-gold to-gold-light text-ocean shadow-lg scale-105'
                      : 'border-ocean/30 bg-white/60 text-ocean hover:border-gold/50 hover:bg-white/80'
                  )}
                  data-testid={`city-${city}`}
                >
                  {formData.destination_cities.includes(city) && <Check className="w-3 h-3 inline mr-1" />}
                  {city}
                </button>
              ))}
            </div>
            
            <div className="mt-3 pt-3 border-t border-ocean/10">
              <Label className="text-ocean/70 text-xs mb-1.5 block">Or enter a city not listed above:</Label>
              <div className="flex gap-2">
                <Input
                  value={customCityInput}
                  onChange={(e) => setCustomCityInput(e.target.value)}
                  placeholder={`Enter city in ${country}`}
                  className="flex-1 h-9 text-sm bg-white/70 border-ocean/20"
                  data-testid={`custom-city-input-${country}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customCityInput.trim()) {
                      e.preventDefault();
                      const city = customCityInput.trim();
                      if (!formData.destination_cities.includes(city)) {
                        setFormData(prev => ({
                          ...prev,
                          destination_cities: [...prev.destination_cities, city],
                          destination_city: prev.destination_city || city,
                        }));
                      }
                      setCustomCityInput('');
                    }
                  }}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const city = customCityInput.trim();
                    if (city && !formData.destination_cities.includes(city)) {
                      setFormData(prev => ({
                        ...prev,
                        destination_cities: [...prev.destination_cities, city],
                        destination_city: prev.destination_city || city,
                      }));
                    }
                    setCustomCityInput('');
                  }}
                  disabled={!customCityInput.trim()}
                  className="bg-gold hover:bg-gold-light text-ocean h-9 px-4 text-sm"
                  data-testid={`add-custom-city-${country}`}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        ))}

        {formData.destination_cities.length > 0 && (
          <div className="mt-4 p-5 bg-gradient-to-br from-ocean/15 to-gold/15 rounded-xl border border-ocean/30 backdrop-blur-sm">
            <p className="text-sm text-ocean/70 mb-2">Your destinations</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Plane className="w-5 h-5 text-gold" />
              <span className="font-serif text-xl text-ocean">
                {formData.destination_cities.join(' \u2192 ')}
              </span>
            </div>
            {formData.destination_countries.length > 1 && (
              <p className="text-xs text-ocean/70 mt-2">
                Multi-country trip across {formData.destination_countries.join(', ')}
              </p>
            )}
            {formData.destination_cities.length > 1 && (
              <p className="text-xs text-ocean/70 mt-1">
                Days will be distributed across all cities
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
